<template>
  <VDataTable
    ref="vDataTable"
    v-bind="$props"
    :headers="internalHeaders"
    :items="internalItems"
    :item-key="internalItemKey"
    :class="computedClass"
    :style="computedStyle"
    :hideDefaultFooter="hidePaginationFooter"
    fixed-header
    multi-sort
    :items-per-page="items.length"
    :server-items-length="isLazy ? (totalHits > 0 ? totalHits : 9999) : undefined"
    :no-data-text="$t('Components.sentences.noData')"
    :no-results-text="$t('Components.sentences.noResults')"
    :options="{
      aggregations: aggregations,
      sortBy: sortBy? sortBy : [],
      sortDesc: sortDesc? sortDesc : [],
      furtherInformations: {
        maxTotalHits: maxTotalHits,
        totalHits: totalHits,
      },
    }"
    v-on="$listeners"
  >
    <template
      v-for="(_, name) in $scopedSlots"
      :slot="name"
      slot-scope="slotData"
    >
      <slot
        v-bind="slotData"
        :name="name"
      />
    </template>

    <template
      v-for="(header, index) in relevantHeaders"
      #[`item.${header.value}`]="{ value, item }"
    >
      <slot
        v-if="$scopedSlots[`item.${header.value}__callback`]"
        :name="`item.${header.value}__callback`"
        :value="transformValue(header, item, value)"
        :item="item"
      />
      <td
        v-else-if="header.alignValue"
        :key="index"
        :class="`text-${header.alignValue}`"
      >
        {{ transformValue(header, item, value) }}
      </td>
      <template v-else>
        {{ transformValue(header, item, value) }}
      </template>
    </template>

    <template
      v-if="withDefaultFooter"
      #body.append="{ headers, options }"
    >
      <WorkDataTableFooter
        :headers="headers"
        :aggregations="options.aggregations"
      >
        <template
          v-for="(_, slot) in dynamicScopedSlots($scopedSlots, 'footer-inner-append')"
          :slot="slot"
          slot-scope="slotData"
        >
          <slot
            v-bind="slotData"
            :name="slot"
          />
        </template>
      </WorkDataTableFooter>
    </template>

    <template
      v-if="pending"
      #body="{ headers }"
    >
      <WorkTableBodySkeleton :headers="headers" />
    </template>
  </VDataTable>
</template>


<script>
import { VDataTable } from 'vuetify/lib';
import {
  dynamicScopedSlots,
} from '@/utils/functional-component-utils';
import WorkDataTableFooter from '@/components/base_components/tables/components/WorkDataTableFooter';
import { truncate } from '@/services/truncate-service';
import WorkTableBodySkeleton from '@/components/base_components/skeletons/WorkTableBodySkeleton';
import pendingAble from '@/mixins/component/pending-able-mixin';
import addUniqueIdentifier from '@/components/base_components/tables/services/add-unique-identifier';

export default {
  name: 'WorkDataTable',
  components: {
    VDataTable,
    WorkDataTableFooter,
    WorkTableBodySkeleton,
  },
  mixins: [pendingAble],
  props: {
    ...VDataTable.options.props,
    initialHeaders: Array,
    cacheId: String,
    isClickable: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: { type: Boolean, default: false },
    selected: Array,
    dense: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    hidePaginationFooter: { type: Boolean, default: true },
    withDefaultFooter: { type: Boolean, default: false },
    numberOfEntriesColspan: { type: Number, required: false },
    isLazy: { type: Boolean, default: false },
    totalHits: { type: Number, required: false },
    maxTotalHits: { type: Number, required: false },
    aggregations: { type: Object, required: false },
    withInputFields: { type: Boolean, default: false },
    truncateData: { type: Boolean, default: true },
    truncateOptions: { type: Object, default: () => { } },
    addUniqueIdentifier: { type: Boolean, default: false }, // if true => item-key will be set to handed 'uniquePropKey'
    uniqueIdentifierKey: { type: String, default: 'composedUnique' },
    readonly: { type: Boolean, default: false },
    hideReadonlyHeaders: { type: Array, default: () => ['actions'] }, // filter with values,
    hideHeaders: { type: Array, default: () => [] },
    hasError: { type: Boolean, default: false },
    hasMaxHeight: { type: Boolean, default: true },
    maxHeight: { type: String, default: '25rem' },
    nextToEachOtherOnMobile: { type: Boolean, default: false }
  },
  data() {
    return {
      dynamicScopedSlots: dynamicScopedSlots,
      internalItems: this.items,
    };
  },
  computed: {
    internalHeaders() {
      let headers = this.headers;

      if (this.readonly && this.hideReadonlyHeaders.length > 0)
        headers = this.headers.filter(
          (header) => !this.hideReadonlyHeaders.includes(header.value)
        );

      if (this.hideHeaders.length > 0)
        headers = this.headers.filter(
          (header) => !this.hideHeaders.includes(header.value)
        );

      return headers;
    },
    internalItemKey() {
      if (this.addUniqueIdentifier) {
        return this.uniqueIdentifierKey;
      }

      return this.itemKey;
    },
    computedClass() {
      return {
        'w-data-table': true,
        'w-data-table--clickable': this.isClickable,
        'w-data-table--hide-header': this.hideDefaultHeader,
        'w-data-table--disabled': this.disabled || this.loading,
        'w-data-table--compact': this.compact,
        'w-data-table--with-input': this.withInputFields,
        'w-data-table--truncate-data': this.truncateData,
        'w-data-table--wrap-data': !this.truncateData,
        'w-data-table--error': this.hasError,
        'w-data-table--next_to_each_other': this.nextToEachOtherOnMobile
      };
    },
    computedStyle() {
      return {
        height: '100%',
        maxHeight: this.hasMaxHeight ? this.maxHeight : '',
      };
    },
    relevantHeaders() {
      return this.headers.filter(
        (header) =>
          header.presentWith != undefined ||
          this.truncateOptions?.[header.value] != undefined
      );
    },
  },
  methods: {
    transformValue(header, item, value) {
      if (header.presentWith)
        value = header.value
          ? header.presentWith(value, item)
          : header.presentWith(item);

      if (
        this.truncateOptions?.[header.value] != undefined &&
        this.truncateData
      )
        value = truncate(
          value,
          this.truncateOptions[header.value].length,
          undefined,
          this.truncateOptions[header.value].unit
        );

      return value;
    },
    clearSelection() {
      this.$refs.vDataTable.selection = [];
    }
  },
  watch: {
    items: {
      handler(newVal) {
        if (this.addUniqueIdentifier) {
          this.internalItems = addUniqueIdentifier(
            newVal,
            this.uniqueIdentifierKey
          );
        } else {
          this.internalItems = newVal;
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@material/elevation/mdc-elevation';
@import 'src/styles/app/reusable-styling';

/* ### TYPOGRAPHY ### */
::v-deep.w-data-table .v-data-table__wrapper {
  .v-data-table-header {

    th {
      *:not(.v-simple-checkbox, .v-simple-checkbox *, .v-btn *) {
        font-size: 0.875rem !important;
        font-weight: $h5-weight;
        color: $table-header-color;
      }
    }
  }
}

::v-deep.w-data-table tbody tr td {
  color: $table-text-color;
}

::v-deep.w-data-table.w-data-table--error .v-data-table__wrapper {
  color: var(--v-error-base) !important;

  .v-data-table-header th {
    *:not(.v-simple-checkbox *) {
      color: var(--v-error-base) !important;
    }
  }
}

/* ### SPACING ROWS ### */
::v-deep.w-data-table thead tr:last-child th {

  // spacing header row
  &:first-child {
    padding-left: $grid-gutter;
  }

  &:last-child {
    padding-right: $grid-gutter;
  }
}

::v-deep.w-data-table tbody tr td {

  // spacing data rows
  &:first-child {
    padding-left: $grid-gutter;
  }

  &:last-child {
    padding-right: $grid-gutter;
  }
}

::v-deep.w-data-table .v-data-table__checkbox svg {
  @include w-checkbox;
}

/* ### TABLE ### */
::v-deep.w-data-table {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: $table-bg-color;

  & .mdi-minus-box::before,
  .mdi-checkbox-marked::before {
    color: $button-color;
  }

  &--compact .v-data-table__wrapper tbody td {
    font-size: $w-p3-font-size !important;
    height: $w-p3-font-size !important;
  }

  &--with-input {

    tbody td,
    tbody td * {
      vertical-align: middle;
      font-size: 1rem !important;

      .v-input__prepend-inner,
      .v-input__append-inner {
        margin: auto;
      }
    }

    tbody td {
      padding-top: calc(#{$spacer} * 2) !important;
      padding-bottom: calc(#{$spacer} * 2) !important;
    }
  }

  .v-data-table__wrapper {
    height: 100%;
  }
}

/* ### TABLE HEADER ### */
::v-deep.w-data-table .v-data-table-header {
  th {
    // avoid display content (icons, label) among each other
    white-space: nowrap !important;
    height: 3.5rem;
    background: $table-bg-color !important;
  }

  .v-data-table-header__icon {
    padding-left: 0.1rem;
    padding-right: 0.1rem;
    vertical-align: text-bottom;
  }
}

/* ### TABLE BODY ### */
::v-deep.w-data-table tbody {
  .v-row-group__header .v-btn--icon {
    background-color: transparent !important;
  }

  tr:not(:last-child) td {
    border-bottom: $table_row_border_bottom_line;
  }
}

/* ### MOBILE TABLE */
::v-deep.w-data-table.v-data-table--mobile.w-data-table--next_to_each_other .v-data-table__mobile-table-row {
  display: flex;

  td {
    flex: 1 1 0px;
    border-top: 0;
    border-bottom: 0;
    justify-content: start;
  }
}

::v-deep.w-data-table.v-data-table--mobile .v-row-group__header {
  white-space: pre-line !important;

  td {
    border-bottom: 0 !important;
  }
}

::v-deep.w-data-table .v-data-table__mobile-table-row {
  &:nth-child(2n) {
    .v-data-table__mobile-row {
      background: $table-odd-bg-color;
    }
  }

  .v-data-table__mobile-row {
    border-bottom: $table_row_border_bottom_line !important;
    padding-left: 1em !important;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &:first-child {
      border-top: 0.06em solid var(--v-header-base);
    }

    .v-data-table__mobile-row__cell {
      word-wrap: break-word;
      white-space: normal;
      text-align: left !important;
    }
  }
}

/* ### TABLE STATES ### */
::v-deep.w-data-table--disabled tbody {
  opacity: $disabled-opacity;
}

::v-deep.w-data-table:not(.w-data-table--clickable) {
  & tbody>tr:hover {
    background: $table-default-hover-color !important;
  }
}

::v-deep.w-data-table .v-data-table__empty-wrapper:hover {
  background: $table-default-hover-color !important;
}

::v-deep.w-data-table--clickable {
  & tbody>tr:not(.v-data-table__empty-wrapper) {
    cursor: pointer;

    &:hover {
      background: $table-hover-color !important;
    }
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  ::v-deep.w-data-table .v-data-table__empty-wrapper>td {
    max-width: 100%;
  }
}

::v-deep.w-data-table--hide-header {
  border-radius: 0;

  & tbody>tr:first-child>td {
    border-top: 0.13em solid var(--v-border-lighten2) !important;
  }
}

/* ### HOW TO HANDLE COLUMN VALUE ### */
::v-deep.w-data-table.w-data-table--truncate-data {
  tbody tr {
    white-space: nowrap !important;
  }
}

::v-deep.w-data-table.w-data-table--wrap-data {
  tbody tr {
    //white-space: pre-wrap !important;
  }
}
</style>
